import type { JSX, PropsWithChildren } from 'react';

import { ThemeProvider } from '@change/ds-core-ui/next';

import { Text } from '@change-corgi/design-system-legacy/typography';
import type { QrCodeType } from '@change-corgi/middle-end-gql-schema';

import { usePetitionQRCode } from 'src/app/shared/hooks/qrcode';

type PetitionQRCodeDownloadLinkProps = PropsWithChildren<{
	slugOrId: string;
	type: QrCodeType;
	size: number;
	filename?: string;
	onDownload?: () => Promise<void>;
}>;
/**
 * Renders a link to download a QR code with the given value.
 * The QRCode is not visible, but is used to download a hi-res version of the QRCode
 */
export function PetitionQRCodeDownloadLink({
	children,
	slugOrId,
	size,
	type,
	filename,
	onDownload,
}: PetitionQRCodeDownloadLinkProps): JSX.Element {
	const { data } = usePetitionQRCode({ slugOrId, size, type, adjustForPixelRatio: false });

	const handleDownload = async () => {
		await onDownload?.();
	};

	return (
		<ThemeProvider>
			{/* We can't use the Link component here because the href becomes the
			    page url with data attribute appended, which breaks the download. */}
			{/* eslint-disable-next-line react/forbid-elements */}
			<a
				href={data?.imageUri ?? '#'}
				download={filename ?? true}
				onClick={handleDownload}
				// Using hex color because we don't have an equivalent color in the new design system
				className="font-bold text-[#006ba9]"
				data-qa="qrcode-download-link"
				data-testid="qrcode-download-link"
			>
				<Text size="small">{children}</Text>
			</a>
		</ThemeProvider>
	);
}
